import { useState, useMemo } from "react";
import { useToken } from "@/pages/token-v2/hooks/use-token";
import { formatToDecimals } from "@/utils/currency";

interface SellBotActionsState {
  amountToken: string;
  isInstant: boolean;
  triggerPrice: string;
  sellGas: string;
  youSell: string;
  youSellPercentage: string;
  youReceive: string;
  youReceiveUSD: string;
  limit: string;
}

export const useSellBotActions = () => {
  const token = useToken();
  const [state, setState] = useState<SellBotActionsState>({
    amountToken: "",
    isInstant: true,
    triggerPrice: "",
    sellGas: "",
    youSell: "",
    youSellPercentage: "",
    youReceive: "",
    youReceiveUSD: "",
    limit: "",
  });

  const pairInfo = useMemo(() => {
    if (!token) {
      return { nativePrice: 0, priceInNative: 0 };
    }
    return token.botPair;
  }, [token]);

  const handleYouSellChange = (value: string, balancePercent?: string) => {
    const youSell = Number(value);
    const numValue = isNaN(youSell) ? 0 : youSell;

    let youReceiveNative = 0;
    let youReceiveUsd = 0;

    if (state.isInstant) {
      youReceiveNative = numValue * pairInfo.priceInNative;
      youReceiveUsd = youReceiveNative * pairInfo.nativePrice;
    } else if (state.triggerPrice) {
      const triggerPrice = Number(state.triggerPrice);
      if (!isNaN(triggerPrice) && triggerPrice > 0) {
        const youSellUsd = numValue * triggerPrice;
        youReceiveNative = youSellUsd / pairInfo.nativePrice;
        youReceiveUsd = youReceiveNative * pairInfo.nativePrice;
      }
    }

    let balancePercentValue = "";
    const tokenBalance = Number(token?.balance ?? 0);

    if (typeof balancePercent === "string") {
      balancePercentValue = balancePercent;
    } else if (tokenBalance > 0) {
      const balancePercentValueNum = (youSell * 100) / tokenBalance;
      balancePercentValue =
        balancePercentValueNum > 0
          ? formatToDecimals(balancePercentValueNum)
          : "0";
    }

    setState((prev) => ({
      ...prev,
      youSell: value,
      youReceive:
        youReceiveNative === 0 ? "0" : formatToDecimals(youReceiveNative, 8),
      youReceiveUSD:
        youReceiveUsd > 0 ? formatToDecimals(youReceiveUsd, 4) : "0",
      youSellPercentage: balancePercentValue,
    }));
  };

  const handleYouSellPercentageChange = (value: string) => {
    const amount = (Number(token?.balance ?? 0) * Number(value)) / 100;

    handleYouSellChange(String(amount), value);
  };

  const handleYouReceiveChange = (value: string) => {
    const youReceiveNative = Number(value);
    const validReceive = !isNaN(youReceiveNative) && youReceiveNative > 0;

    let youSell = 0;
    let youSellPercentage = 0;
    const tokenBalance = Number(token?.balance ?? 0);
    const youReceiveUsd = youReceiveNative * pairInfo.nativePrice;

    if (validReceive) {
      if (state.isInstant && pairInfo.priceInNative > 0) {
        youSell = youReceiveNative / pairInfo.priceInNative;
      } else if (!state.isInstant && Number(state.triggerPrice) > 0) {
        const triggerPrice = Number(state.triggerPrice);
        youSell = (youReceiveNative * pairInfo.nativePrice) / triggerPrice;
      }

      if (tokenBalance > 0) {
        youSellPercentage = (youSell * 100) / tokenBalance;
      }
    }

    setState((prev) => ({
      ...prev,
      youReceive: value,
      youReceiveUSD:
        youReceiveUsd > 0 ? formatToDecimals(youReceiveUsd, 4) : "0",
      youSell: youSell === 0 ? "0" : formatToDecimals(youSell, 8),
      youSellPercentage:
        youSellPercentage > 0 ? formatToDecimals(youSellPercentage) : "0",
    }));
  };

  const handleYouReceiveUSDChange = (value: string) => {
    const youReceiveUsd = Number(value);
    const validUsd = !isNaN(youReceiveUsd) && youReceiveUsd > 0;

    let youReceiveNative = 0;
    let youSell = 0;
    let youSellPercentage = 0;

    const tokenBalance = Number(token?.balance ?? 0);

    if (validUsd && pairInfo.nativePrice > 0) {
      youReceiveNative = youReceiveUsd / pairInfo.nativePrice;

      if (state.isInstant && pairInfo.priceInNative > 0) {
        youSell = youReceiveNative / pairInfo.priceInNative;
      } else if (!state.isInstant && Number(state.triggerPrice) > 0) {
        const triggerPrice = Number(state.triggerPrice);
        youSell = (youReceiveNative * pairInfo.nativePrice) / triggerPrice;
      }

      if (tokenBalance > 0) {
        youSellPercentage = (youSell * 100) / tokenBalance;
      }
    }

    setState((prev) => ({
      ...prev,
      youReceiveUSD: value,
      youReceive: formatToDecimals(youReceiveNative, 8),
      youSell: youSell === 0 ? "0" : formatToDecimals(youSell, 8),
      youSellPercentage:
        youSellPercentage > 0 ? formatToDecimals(youSellPercentage) : "0",
    }));
  };

  const handleTriggerPriceChange = (value: string) => {
    const parsedTriggerPrice = Number(value);
    const validTrigger = !isNaN(parsedTriggerPrice) && parsedTriggerPrice > 0;

    const youSell = Number(state.youSell);
    const validYouSell = !isNaN(youSell) && youSell > 0;

    let youReceiveNative = 0;
    let youReceiveUsd = 0;

    if (validTrigger && validYouSell) {
      const youSellUsd = youSell * parsedTriggerPrice;

      if (pairInfo.nativePrice > 0) {
        youReceiveNative = youSellUsd / pairInfo.nativePrice;
        youReceiveUsd = youReceiveNative * pairInfo.nativePrice;
      }
    }

    setState((prev) => ({
      ...prev,
      triggerPrice: value,
      youReceive: formatToDecimals(youReceiveNative, 8),
      youReceiveUSD:
        youReceiveUsd > 0 ? formatToDecimals(youReceiveUsd, 4) : "0",
    }));
  };

  const handleInstantChange = (value: boolean) => {
    if (value) {
      setState((prev) => ({ ...prev, limit: "", isInstant: true }));
    } else {
      setState((prev) => ({ ...prev, isInstant: false }));
    }
  };

  const handleLimitChange = (limit: string) => {
    if (limit === "") {
      setState((prev) => ({
        ...prev,
        isInstant: true,
        limit: "",
      }));
      handleTriggerPriceChange("");
    } else {
      const limitValue = isNaN(Number(limit)) ? 0 : Number(limit);
      const price = (token?.price ?? 0) * (1 + limitValue / 100);

      setState((prev) => ({
        ...prev,
        isInstant: false,
        limit,
      }));
      handleTriggerPriceChange(String(price));
    }
  };

  return {
    state,
    setState,
    handlers: {
      handleYouSellChange,
      handleYouSellPercentageChange,
      handleYouReceiveChange,
      handleYouReceiveUSDChange,
      handleTriggerPriceChange,
      handleInstantChange,
      handleLimitChange,
    },
  };
};
